import { useState, type FormEvent } from 'react'
import { useMemo } from 'react'
import { postContact } from '../api/public'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { schemaContactPage, schemaGraph } from '../lib/schema-org'

const SUBJECTS = [
  { key: 'Anfrage Booking', label: 'Booking' },
  { key: 'Anfrage Event', label: 'Event' },
  { key: 'DJ Set Submissions', label: 'DJ Submissions' },
  { key: 'Verein', label: 'Verein' },
  { key: 'Allgemeines', label: 'Allgemeines', wide: true },
] as const

export function ContactPage() {
  useCrtPageTitle('Kontakt')

  const jsonLd = useMemo(() => schemaGraph(...schemaContactPage()), [])
  usePageMeta({
    title: 'Kontakt',
    description: 'Kontakt zu BNKR — Booking, Events, DJ Submissions und Verein.',
    canonical: '/contact',
    jsonLd,
  })

  const [subject, setSubject] = useState<string>('Anfrage Booking')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSending(true)
    try {
      await postContact({ name, email, subject, message })
      setDone(true)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Senden fehlgeschlagen.')
    } finally {
      setSending(false)
    }
  }

  function reset() {
    setDone(false)
    setName('')
    setEmail('')
    setMessage('')
    setSubject('Anfrage Booking')
  }

  return (
    <section className="section" style={{ paddingBottom: 'var(--sp-16)', paddingTop: 'var(--sp-4)' }}>
      <div className="wrap">
        <section id="kontakt">
          <div className="kicker crt">↳ /kontakt</div>
          <h1 className="display h2" style={{ marginTop: 14 }}>
            Kontakt
          </h1>

          {!done ? (
            <div className="grid-2" style={{ marginTop: 48, alignItems: 'start' }}>
              <div>
                <div className="kicker" style={{ margin: '0 0 8px' }}>
                  Telefon
                </div>
                <a className="mono crt" style={{ fontSize: 16, textDecoration: 'underline', textUnderlineOffset: 4 }} href="tel:+41797120918">
                  +41 79 712 09 18
                </a>

                <div className="kicker" style={{ margin: '32px 0 8px' }}>
                  E-Mail
                </div>
                <a className="mono crt" style={{ fontSize: 16, textDecoration: 'underline', textUnderlineOffset: 4 }} href="mailto:info@bnkr.ch">
                  info@bnkr.ch
                </a>

                <div className="kicker" style={{ margin: '32px 0 8px' }}>
                  Links
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <a className="lnk" href="https://links.bnkr.ch" target="_blank" rel="noreferrer">
                    links.bnkr.ch ↗
                  </a>
                  <a className="lnk" href="https://foursix.ch/collections/bnkr" target="_blank" rel="noreferrer">
                    Shop ↗
                  </a>
                </div>
              </div>

              <div>
                <div style={{ marginBottom: 24 }}>
                  <div className="kicker" style={{ marginBottom: 12 }}>
                    Betreff wählen
                  </div>
                  <div className="topic-grid">
                    {SUBJECTS.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        className={`topic${subject === s.key ? ' active' : ''}`}
                        style={s.wide ? { gridColumn: '1 / -1' } : undefined}
                        onClick={() => setSubject(s.key)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <form className="form-grid" onSubmit={onSubmit}>
                  {err ? (
                    <p className="error" style={{ gridColumn: '1 / -1' }}>
                      {err}
                    </p>
                  ) : null}
                  <div className="field">
                    <label htmlFor="contact-name">Name</label>
                    <input
                      className="input"
                      id="contact-name"
                      type="text"
                      placeholder="Max Muster"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="contact-email">E-Mail</label>
                    <input
                      className="input"
                      id="contact-email"
                      type="email"
                      placeholder="max@example.ch"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="field" style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="contact-message">Nachricht</label>
                    <textarea
                      className="textarea"
                      id="contact-message"
                      placeholder="Deine Nachricht…"
                      rows={5}
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" className="btn btn-primary" disabled={sending}>
                      {sending ? 'Senden…' : 'Absenden →'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 48, maxWidth: 480 }}>
              <div className="manifest">
                <div className="ln">
                  <span className="a">›</span>
                  <span className="ok">Nachricht gesendet</span>
                </div>
                <div className="ln">
                  <span className="a">›</span>
                  <span>Wir melden uns so bald wie möglich.</span>
                </div>
              </div>
              <button type="button" className="btn btn-crt" style={{ marginTop: 24 }} onClick={reset}>
                Neue Anfrage
              </button>
            </div>
          )}
        </section>
      </div>
    </section>
  )
}
